/**
 * AWARE Health Frontend — Forecast Map v4
 *
 * Two rendering modes:
 *  ① GLOBAL MODE (no country selected):
 *     Animated CircleMarker pins for VN / TW / NP.
 *  ② CHOROPLETH MODE (any country selected, VN / TW / NP):
 *     Admin-level GeoJSON heatmap using CountryChoropleth.
 *     - Automatic zoom to bounding box on click
 *     - Highlighted hover feature on map & synchronized with sidebar hover
 */

import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet'
import { useEffect } from 'react'
import CountryChoropleth from './CountryChoropleth'
import type { ForecastRecord } from '../../types/forecast'
import { useTheme } from '../../contexts/ThemeContext'

const COUNTRY_PINS = [
  { code: 'VN', name: 'Vietnam', lat: 16.0, lng: 107.8, issueDate: '2026-W31' },
  { code: 'TW', name: 'Taiwan',  lat: 23.7, lng: 121.0, issueDate: '2026-W31' },
  { code: 'NP', name: 'Nepal',   lat: 28.4, lng: 84.1,  issueDate: '2026-W31' },
]

const RISK_COLORS: Record<string, string> = {
  high: '#ef4444', medium: '#f59e0b', low: '#10b981', none: '#14b8a6',
}

// Countries with choropleth support
const CHOROPLETH_COUNTRIES = new Set(['VN', 'TW', 'NP'])

interface ProvinceData {
  regionId: string
  record: ForecastRecord
}

interface ForecastMapProps {
  selectedCountry: string | null
  onRegionSelect: (code: string, issueDate: string) => void
  onProvinceClick: (regionId: string) => void
  onProvinceHover?: (regionId: string | null) => void
  selectedRegionId: string | null
  hoveredRegionId?: string | null
  riskByCountry?: Record<string, string>
  alertByCountry?: Record<string, string>
  forecastByRegionName?: Map<string, ProvinceData>
}

function MapFlyTo({ selectedCountry }: { selectedCountry: string | null }) {
  const map = useMap()
  useEffect(() => {
    if (!selectedCountry) {
      map.flyTo([18, 100], 4, { duration: 1.0 })
    }
    // Choropleth component handles its own fly-to on mount
  }, [selectedCountry, map])
  return null
}

export default function ForecastMap({
  selectedCountry,
  onRegionSelect,
  onProvinceClick,
  onProvinceHover = () => {},
  selectedRegionId,
  hoveredRegionId = null,
  riskByCountry = {},
  alertByCountry = {},
  forecastByRegionName,
}: ForecastMapProps) {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  
  const showChoropleth = !!selectedCountry &&
    CHOROPLETH_COUNTRIES.has(selectedCountry) &&
    !!forecastByRegionName

  return (
    <MapContainer
      center={[18, 100]}
      zoom={4}
      className="w-full h-full"
      style={{ background: isDark ? '#030712' : '#e2e8f0', borderRadius: 12 }}
      scrollWheelZoom
      zoomControl={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={19}
      />

      <MapFlyTo selectedCountry={selectedCountry} />

      {showChoropleth && (
        <CountryChoropleth
          countryCode={selectedCountry!}
          forecastByRegionName={forecastByRegionName!}
          selectedRegionId={selectedRegionId}
          hoveredRegionId={hoveredRegionId}
          onProvinceClick={onProvinceClick}
          onProvinceHover={onProvinceHover}
        />
      )}

      {COUNTRY_PINS.map((pin) => {
        const isSelected = selectedCountry === pin.code
        const riskColor  = RISK_COLORS[riskByCountry[pin.code] ?? 'none']
        const alertLevel = alertByCountry[pin.code]
        const dimmed     = showChoropleth && !isSelected

        return (
          <div key={pin.code}>
            {isSelected && !showChoropleth && (
              <CircleMarker
                center={[pin.lat, pin.lng]}
                radius={26}
                pathOptions={{ fillColor: riskColor, color: riskColor, weight: 1, fillOpacity: 0.08, opacity: 0.3 }}
                interactive={false}
              />
            )}
            {isSelected && !showChoropleth && (
              <CircleMarker
                center={[pin.lat, pin.lng]}
                radius={18}
                pathOptions={{ fillColor: riskColor, color: riskColor, weight: 1, fillOpacity: 0.13, opacity: 0.45 }}
                interactive={false}
              />
            )}

            <CircleMarker
              center={[pin.lat, pin.lng]}
              radius={isSelected ? 12 : 8}
              pathOptions={{
                fillColor: riskColor,
                color:       isSelected ? '#ffffff' : riskColor,
                weight:      isSelected ? 2.5 : 1.5,
                fillOpacity: dimmed ? 0.25 : isSelected ? 0.95 : 0.7,
                opacity:     dimmed ? 0.3 : 1,
              }}
              eventHandlers={{ click: () => onRegionSelect(pin.code, pin.issueDate) }}
            >
              <Tooltip direction="top" offset={[0, -12]} permanent={!dimmed} opacity={dimmed ? 0.4 : 1}>
                <div style={{ textAlign: 'center', minWidth: 80 }}>
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{pin.name}</div>
                  {alertLevel && alertLevel !== 'None' ? (
                    <div
                      style={{
                        fontSize: 10, marginTop: 2, fontWeight: 600,
                        color: alertLevel === 'Emergency' ? '#a855f7' :
                               alertLevel === 'Warning'   ? '#ef4444' :
                               alertLevel === 'Watch'     ? '#f59e0b' : '#64748b',
                      }}
                    >
                      {alertLevel === 'Emergency' ? '⚠ ' : alertLevel === 'Warning' ? '▲ ' : '◆ '}
                      {alertLevel}
                    </div>
                  ) : (
                    <div style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>
                      {isSelected && showChoropleth ? 'Viewing regions' : 'Click to explore'}
                    </div>
                  )}
                </div>
              </Tooltip>
            </CircleMarker>
          </div>
        )
      })}
    </MapContainer>
  )
}
